/**
 * Downscale phone photos before upload: CI and voucher photos straight from a
 * camera are 4–10 MB, too slow on mobile data at the event. 1600px keeps small
 * print (CI numbers, transaction ids) legible — same floor as prepareVoucherImage.
 * PDFs and small images pass through untouched.
 */

const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.82;
const SKIP_BELOW_BYTES = 400 * 1024;

export async function compressImageForUpload(file: File): Promise<Blob> {
  if (!file.type.startsWith('image/') || file.size < SKIP_BELOW_BYTES) return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file; // Unsupported format (e.g. HEIC on some browsers) — upload as-is
  }

  const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    return file;
  }
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
  return blob && blob.size < file.size ? blob : file;
}
